import { Draft } from 'immer';
import { AppState } from '../interface';
import { OverlayContent } from '../../../enums/elementId';
import { getNewSelectedMetacode } from 'Container/AvailabilityCheck/util/providerSwitcher';
import { ProviderType } from '../../../enums/product';
import { AvcResult, JarvisProduct } from '@vfde-sails/avc-core';

export const setSelectedByID = (draft: Draft<AppState>, newSelectedId: string | undefined) => {
    if (!newSelectedId) {
        return;
    }

    draft.selected.tariffId = newSelectedId;
    draft.selected.tariff = draft.tariffs[draft.activeProviderType]
        .find(tariff => tariff.id === newSelectedId);
};

export const getFastestProduct = (products: JarvisProduct[]): JarvisProduct | undefined =>
    products.reduce((fastest, product) => {
        return +product.download > +fastest.download ? product : fastest;
    }, products[0]);

/**
 * Find and set selected Tariff by selected tariffId
 * If not available find and set fastest available tariff
 *
 * @param draft AppState
 * @param tariffs JarvisProduct[]
 */
const setSelectedTariff = (draft: Draft<AppState>, tariffs: JarvisProduct[]) => {
    if (0 === tariffs.length) {
        return;
    }

    const { tariffId } = draft.selected;
    const tariff = tariffs.find(t => t.id === tariffId);
    const fastestTariff = getFastestProduct(tariffs);

    if (tariffId && tariff) {
        draft.selected.tariff = tariff;
    } else if (fastestTariff) {
        draft.selected.tariff = fastestTariff;
        draft.selected.tariffId = fastestTariff.id;
    }
};

/**
 * Sets given tariffs based on the currently selected product
 * and sets new selected Tariff is current one is not available
 *
 * @param draft State
 * @param tariffs JarvisProduct[]
 */
export const handleSetTariffs = (draft: Draft<AppState>, tariffs: JarvisProduct[]) => {
    // immer freezes the array so we need a shallow copy first
    const sortedByDownload = [...tariffs].sort((t1, t2) => +t1.download > +t2.download ? -1 : 1);

    draft.tariffs[draft.activeProviderType] = sortedByDownload;

    setSelectedTariff(draft, sortedByDownload);
};

/**
 * Sets the avcResult, checked address,
 * and a new selected tariff if the currently selected is not available.
 * If no tariff is available leaves the selection as is.
 *
 * @param draft AppState
 * @param result AvcResult
 */
export const handleSetAvcResult = (draft: Draft<AppState>, result?: AvcResult) => {
    if (!result) {
        draft.address = undefined;
        draft.avcResult = undefined;

        return;
    }

    draft.avcResult = result;
    draft.address = result.address;

    const availableTariffs = draft.tariffs[draft.activeProviderType]
        .filter(tariff => result.metacodes.includes(tariff.id));

    if (availableTariffs.length) {
        setSelectedTariff(draft, draft.tariffs[draft.activeProviderType]);
    }

    draft.activeOverlay = OverlayContent.RESULT;
    draft.loading = false;
};

export const handleInitStore = (draft: Draft<AppState>, state: Partial<AppState>) => {
    state.loading = false;

    draft.gigakombi = state.gigakombi || false;
    draft.familyAndFriends = state.familyAndFriends || false;

    state.address && (draft.address = state.address);
    state.selected && (draft.selected = state.selected);
    state.tariffs && (draft.tariffs = state.tariffs);
    state.avcResult && (draft.avcResult = state.avcResult);
};

export const handleProviderReset = (draft: Draft<AppState>, { activeProviderType }: AppState) => {
    if (draft.activeProviderType === activeProviderType) {
        return;
    }

    const newSelectedId = getNewSelectedMetacode(
        draft.activeProviderType,
        draft.selected.tariffId,
        activeProviderType
    );

    draft.activeProviderType = activeProviderType;
    setSelectedByID(draft, newSelectedId);
};

// TODO: Rework based on PR 66 (and debug this)
export const handleAfterVpLinkReset = (draft: Draft<AppState>) => {
    const providers = Object.keys(draft.tariffs);

    providers.forEach(provider => {
        const providerTariffs = draft.tariffs[provider as ProviderType];

        providerTariffs.forEach((_, i) => {
            providerTariffs[i].afterVpProductInfo = undefined;
        });
    });
};
